import React from "react";
import { ArrowRight, Linkedin, Mail, Phone, MapPin } from "lucide-react";

const LOGO_FLAT = "/logo-si.png";

const COL_COMPANY = [
  { id: "home", label: "Home" },
  { id: "about", label: "About Us" },
  { id: "leadership", label: "Leadership" },
  { id: "values", label: "Our Values" },
  { id: "contact", label: "Contact" },
];
const COL_SERVICES = [
  { id: "capabilities", label: "Capabilities" },
  { id: "quality", label: "Quality Assurance" },
  { id: "compliance", label: "Compliance & Standards" },
  { id: "logistics", label: "Packaging & Logistics" },
];

const FOOT_PILLARS = [
  { k: "Trusted Partner", d: "Reliable solutions through a trusted partner network." },
  { k: "Quality Assured", d: "Committed to quality at every stage." },
  { k: "Global Reach", d: "Delivering excellence across the globe." },
  { k: "Customer Focused", d: "Your success is our top priority." },
  { k: "Innovation Driven", d: "Driven by innovation and continuous improvement." },
];

const scrollTo = (e, id) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Footer = () => {
  return (
    <footer className="bg-[#0A1F44] text-slate-200 relative overflow-hidden" data-testid="site-footer">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12 pt-24 pb-12 relative">
        {/* Top — brand + cols */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <img src={LOGO_FLAT} alt="Samarth Infinity" className="h-16 md:h-20 w-auto mb-10" draggable="false" />
            <p className="font-grotesque text-[10px] tracking-[0.42em] uppercase text-[#E2B548] font-bold" data-testid="footer-eyebrow">
              Partnering Today · Building Tomorrow
            </p>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.02] tracking-[-0.025em] text-white mt-6">
              Engineering Sourcing &<br />
              <span className="font-display-italic text-[#E2B548]">Manufacturing Solutions.</span>
            </h2>
            <p className="text-slate-300 max-w-xl mt-6 leading-[1.75]">
              Carefully packaged. Safely delivered. Globally connected.
            </p>
            <a href="#contact" onClick={(e) => scrollTo(e, "contact")} data-testid="footer-cta" className="inline-flex items-center gap-3 mt-10 group">
              <span className="font-display-italic text-2xl md:text-3xl text-white group-hover:text-[#E2B548] transition-colors">
                Let's build a stronger future together
              </span>
              <span className="h-11 w-11 inline-flex items-center justify-center border border-white/40 group-hover:border-[#E2B548] group-hover:bg-[#E2B548] transition-all">
                <ArrowRight className="h-4 w-4 text-white group-hover:text-[#0A1F44]" />
              </span>
            </a>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-10">
            <div>
              <p className="font-grotesque text-[10px] tracking-[0.34em] uppercase text-[#E2B548] font-bold mb-5">Company</p>
              <ul className="space-y-3">
                {COL_COMPANY.map((c) => (
                  <li key={c.label}>
                    <a href={`#${c.id}`} onClick={(e) => scrollTo(e, c.id)} className="text-slate-300 hover:text-white transition-colors text-sm font-grotesque">{c.label}</a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-grotesque text-[10px] tracking-[0.34em] uppercase text-[#E2B548] font-bold mb-5">Solutions</p>
              <ul className="space-y-3">
                {COL_SERVICES.map((c) => (
                  <li key={c.label}>
                    <a href={`#${c.id}`} onClick={(e) => scrollTo(e, c.id)} className="text-slate-300 hover:text-white transition-colors text-sm font-grotesque">{c.label}</a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Pillars band */}
        <div className="mt-20 pt-10 border-t border-white/15">
          <p className="font-grotesque text-[10px] tracking-[0.34em] uppercase text-[#E2B548] font-bold mb-8">
            Why Samarth Infinity
          </p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-white/10 border-t border-b border-white/10">
            {FOOT_PILLARS.map((p, i) => (
              <div key={p.k} className="bg-[#0A1F44] p-6">
                <p className="font-display-italic text-base text-[#E2B548]">0{i + 1}.</p>
                <p className="font-display text-lg text-white mt-3 leading-tight">{p.k}</p>
                <p className="text-xs text-slate-400 mt-3 leading-[1.65]">{p.d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Contact strip */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="flex items-start gap-4">
            <span className="h-11 w-11 inline-flex items-center justify-center border border-white/20"><MapPin className="h-4 w-4 text-[#E2B548]" /></span>
            <div>
              <p className="font-grotesque text-[10px] tracking-[0.32em] uppercase text-slate-400 font-bold mb-1">Headquarters</p>
              <p className="text-slate-100 font-medium">G301, Phase 3, Pristine Green Society,<br />Moshi, Pune 412105</p>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="h-11 w-11 inline-flex items-center justify-center border border-white/20"><Mail className="h-4 w-4 text-[#E2B548]" /></span>
            <div>
              <p className="font-grotesque text-[10px] tracking-[0.32em] uppercase text-slate-400 font-bold mb-1">Email</p>
              <a href="mailto:info@samarthinfinity.com" className="text-slate-100 hover:text-white font-medium">info@samarthinfinity.com</a>
            </div>
          </div>
          <div className="flex items-start gap-4">
            <span className="h-11 w-11 inline-flex items-center justify-center border border-white/20"><Phone className="h-4 w-4 text-[#E2B548]" /></span>
            <div>
              <p className="font-grotesque text-[10px] tracking-[0.32em] uppercase text-slate-400 font-bold mb-1">Phone</p>
              <a href="tel:+919765406783" className="text-slate-100 hover:text-white font-medium">+91 9765406783</a>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/15 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-grotesque text-xs text-slate-400" data-testid="footer-copyright">
            © {new Date().getFullYear()} Samarth Infinity · All rights reserved.
          </p>
          <div className="flex items-center gap-4 font-grotesque text-xs text-slate-400">
            <a href="#" className="hover:text-white inline-flex items-center gap-2"><Linkedin className="h-3.5 w-3.5" /> LinkedIn</a>
            <span className="opacity-30">·</span>
            <span className="tracking-[0.22em] uppercase font-bold">Integrity · Collaboration · Excellence · Growth</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
